/**
 * Account Settings Page
 * @implements FS-001.2.2 - Password Authentication (Backup)
 * @implements US-008b - Password Setup UI (Frontend)
 *
 * Account settings page accessible from the dashboard.
 * Shows user profile information and password management section.
 * Displays appropriate form based on whether user has a password set.
 */

import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Avatar,
  Divider,
  Skeleton,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '@/providers';
import { PasswordSetupForm, PasswordChangeForm } from '@/components/account';

/**
 * AccountSettingsPage - User account settings and password management
 *
 * @implements FS-001.2.2 - Password Authentication (Backup)
 * @implements US-008b - Password Setup UI
 */
export const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const { user, isLoading, refetch } = useAuthContext();

  /**
   * Get the appropriate back navigation path based on user role
   */
  const getBackPath = () => {
    if (!user) return '/';
    if (user.roles.includes('PRODUCT_OWNER')) {
      return '/dashboard/po';
    }
    return '/';
  };

  /**
   * Handle successful password set/change
   * Refetch user data to update hasPassword status
   */
  const handlePasswordSuccess = async () => {
    await refetch();
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={400} />
        </Box>
      </Container>
    );
  }

  // Not authenticated
  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            You must be logged in to access account settings.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Back Navigation */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(getBackPath())}
          sx={{ mb: 3, textTransform: 'none' }}
        >
          Back to Dashboard
        </Button>

        {/* Page Title */}
        <Typography variant="h4" component="h1" gutterBottom>
          Account Settings
        </Typography>

        {/* User Profile Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Profile
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={user.avatarUrl || undefined}
              alt={user.displayName || user.email}
              sx={{ width: 80, height: 80 }}
            >
              {(user.displayName || user.email)[0].toUpperCase()}
            </Avatar>

            <Box>
              <Typography variant="h6">{user.displayName || 'User'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Roles: {user.roles.join(', ')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Password Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Security
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Show appropriate form based on hasPassword status */}
          {user.hasPassword ? (
            <PasswordChangeForm onSuccess={handlePasswordSuccess} />
          ) : (
            <PasswordSetupForm onSuccess={handlePasswordSuccess} />
          )}

          {/* Additional info about password backup */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {user.hasPassword ? (
                <>
                  You have a backup password set. You can use it to sign in if
                  your OAuth provider is temporarily unavailable.
                </>
              ) : (
                <>
                  Setting a backup password is optional but recommended. It
                  allows you to access your account if your OAuth provider
                  experiences downtime.
                </>
              )}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
